/// <reference path="../../../types/axios-fix.d.ts" />
import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { HttpClientService } from './http-client.service';

/**
 * Authentication Interceptor Service
 * 
 * Handles authentication token management, including:
 * - Adding auth headers to requests
 * - Token refresh logic
 * - Token expiration checking
 * - Authentication error handling
 * 
 * Based on patterns from the existing monolithic API service.
 */
export class AuthInterceptorService {
  private accessToken: string | null = null;
  private refreshPromise: Promise<string | null> | null = null;
  private authErrorHandler: (() => void) | null = null;
  private httpClient: HttpClientService;
  private requestInterceptorId: number | null = null;
  private responseInterceptorId: number | null = null;
  private retryCount: Map<string, number> = new Map(); // Track retry attempts per request
  private lastRefreshAttemptAt: number = 0; // Cooldown: don't background-refresh more than once/min

  constructor(httpClient: HttpClientService) {
    this.httpClient = httpClient;
    this.loadTokenFromStorage();
    this.setupInterceptors();
  }

  /**
   * Load token from localStorage (matches existing pattern)
   */
  private loadTokenFromStorage(): void {
    this.accessToken = localStorage.getItem('accessToken');
  }

  /**
   * Setup axios interceptors for authentication
   */
  private setupInterceptors(): void {
    // Request interceptor - add auth header and handle token refresh
    this.requestInterceptorId = this.httpClient.addRequestInterceptor(
      (config: AxiosRequestConfig) => {
        // Check if this request should skip authentication
        const skipAuth = config.headers?.['X-Skip-Auth'] === 'true';
        if (skipAuth) {
          // Remove the X-Skip-Auth header before sending (it's only for internal use)
          delete config.headers?.['X-Skip-Auth'];
          return config;
        }

        // Check if this is an authentication-related request
        const isAuthRequest = config.url?.includes('/api/auth/') || config.url?.includes('/auth/') || false;

        // Always get fresh token from localStorage
        const token = localStorage.getItem('accessToken');

        if (token) {
          // Background refresh when token is near expiration — but at most once per minute
          // to prevent hammering the /auth/refresh endpoint when many parallel requests fire.
          if (this.isTokenNearExpiration(token) && !isAuthRequest) {
            const now = Date.now();
            const REFRESH_COOLDOWN_MS = 60 * 1000;
            if (now - this.lastRefreshAttemptAt > REFRESH_COOLDOWN_MS) {
              this.lastRefreshAttemptAt = now;
              this.refreshAccessToken().catch(() => {});
            }
          }

          config.headers = config.headers || {} as any;
          config.headers['Authorization'] = `Bearer ${token}`;
          this.accessToken = token; // Update cached token
        }

        return config;
      },
      (error) => {
        console.error('AuthInterceptor: Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle token refresh on 401
    this.responseInterceptorId = this.httpClient.addResponseInterceptor(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        console.debug('AuthInterceptor: Response error:', {
          url: error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText
        });

        // Handle 401 unauthorized errors with token refresh
        if (error.response?.status === 401) {
          const isAuthRequest = error.config?.url?.includes('/api/auth/') ||
                               error.config?.url?.includes('/auth/');

          // Check if this is a bookmark request (optional auth)
          const isBookmarkRequest = error.config?.url?.includes('/bookmarks');

          // Public-link endpoints are anonymous by design. They use HTTP 401
          // for domain errors like PASSWORD_REQUIRED, INVALID_PASSWORD,
          // INVALID_TOKEN, TOKEN_EXPIRED — these are NOT authentication
          // failures of the session. Let the originating service surface
          // them to its caller so the UI can show a password prompt or a
          // "link expired" message instead of redirecting to login.
          const isPublicLinkRequest = !!error.config?.url?.includes('/public/');

          if (isPublicLinkRequest) {
            return Promise.reject(error);
          }

          if (!isAuthRequest && this.accessToken) {
            // CIRCUIT BREAKER: Check retry count to prevent infinite loops
            const requestKey = `${error.config?.method}:${error.config?.url}`;
            const currentRetries = this.retryCount.get(requestKey) || 0;

            if (currentRetries >= 2) {
              console.error(`AuthInterceptor: Max retries (${currentRetries}) exceeded for ${requestKey}, breaking loop`);
              this.retryCount.delete(requestKey);

              // For bookmark requests, just reject without redirect
              if (isBookmarkRequest) {
                return Promise.reject(error);
              }

              // Check if we have a fresh token in localStorage before clearing everything
              // This prevents race conditions where parallel requests clear tokens that were just refreshed
              const currentToken = localStorage.getItem('accessToken');
              if (!currentToken) {
                // Only clear tokens if there genuinely is no token
                console.log('AuthInterceptor: No token found after max retries, handling auth error');
                this.handleAuthError();
              } else {
                console.warn('AuthInterceptor: Max retries exceeded but token exists, not clearing tokens');
              }

              return Promise.resolve({ data: null, status: 401, statusText: 'Unauthorized', headers: {}, config: error.config! });
            }

            try {
              console.log(`AuthInterceptor: Attempting token refresh due to 401 (retry ${currentRetries + 1}/2)`);
              this.retryCount.set(requestKey, currentRetries + 1);

              const newToken = await this.refreshAccessToken();

              if (newToken && error.config) {
                // Retry original request with new token
                error.config.headers = error.config.headers || {};
                error.config.headers['Authorization'] = `Bearer ${newToken}`;
                const retryResponse = await this.httpClient.request(error.config);

                // Success! Clear retry count
                this.retryCount.delete(requestKey);
                return retryResponse;
              } else {
                // Token refresh succeeded but no token returned
                console.error('AuthInterceptor: Token refresh returned no token');
                this.retryCount.delete(requestKey);

                // Check if another parallel request successfully refreshed the token
                const currentToken = localStorage.getItem('accessToken');
                if (!currentToken && !isBookmarkRequest) {
                  console.log('AuthInterceptor: No token after refresh, handling auth error');
                  this.handleAuthError();
                } else if (currentToken) {
                  console.log('AuthInterceptor: Token exists from parallel refresh, not clearing');
                }

                // For non-auth requests, return null data to prevent error propagation
                return Promise.resolve({ data: null, status: 401, statusText: 'Unauthorized', headers: {}, config: error.config! });
              }
            } catch (refreshError) {
              console.error('AuthInterceptor: Token refresh failed:', refreshError);
              this.retryCount.delete(requestKey);

              // Check if another parallel request successfully refreshed the token
              const currentToken = localStorage.getItem('accessToken');
              if (!currentToken && !isBookmarkRequest) {
                console.log('AuthInterceptor: Refresh error and no token, handling auth error');
                this.handleAuthError();
              } else if (currentToken) {
                console.log('AuthInterceptor: Refresh error but token exists from parallel refresh');
              }

              // For non-auth requests, return null data to prevent error propagation
              return Promise.resolve({ data: null, status: 401, statusText: 'Unauthorized', headers: {}, config: error.config! });
            }
          } else if (isAuthRequest) {
            // For auth requests, let the original error pass through so the auth service can handle it
            console.log('AuthInterceptor: 401 error on auth request, passing error through to auth service');
            return Promise.reject(error);
          } else if (isBookmarkRequest) {
            // Bookmark requests are optional auth - don't redirect, just reject
            console.log('AuthInterceptor: 401 on bookmark request - no token, rejecting without redirect');
            return Promise.reject(error);
          } else {
            // No token and non-auth request - check if parallel request refreshed
            const currentToken = localStorage.getItem('accessToken');
            if (!currentToken) {
              console.log('AuthInterceptor: 401 error - no token, handling auth error');
              this.handleAuthError();
            } else {
              console.log('AuthInterceptor: 401 error but token now exists from parallel refresh');
            }
            // Return null data to prevent error propagation for non-auth requests
            return Promise.resolve({ data: null, status: 401, statusText: 'Unauthorized', headers: {}, config: error.config! });
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Check if token is near expiration (within 5 minutes)
   */
  private isTokenNearExpiration(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      const eightMinutes = 8 * 60 * 1000;

      return (exp - now) < eightMinutes;
    } catch (error) {
      console.warn('AuthInterceptor: Error checking token expiration:', error);
      return false;
    }
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.warn('AuthInterceptor: No refresh token available');
      return null;
    }

    this.refreshPromise = this.performTokenRefresh(refreshToken);
    
    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(refreshToken: string): Promise<string | null> {
    try {
      console.log('AuthInterceptor: Performing token refresh');
      
      const response = await this.httpClient.post('/auth/refresh', {
        refresh_token: refreshToken
      });

      // Handle both camelCase and snake_case field names
      // The v2 API returns snake_case in the data object
      const responseData = response.data?.data || response.data;
      
      // Check if this is an error response
      if (responseData?.error) {
        throw new Error(responseData.error.message || 'Token refresh failed');
      }
      
      // Safely access token properties with null checking
      const newAccessToken = responseData?.access_token || responseData?.accessToken;
      const newRefreshToken = responseData?.refresh_token || responseData?.refreshToken;

      if (newAccessToken) {
        // Update stored tokens
        localStorage.setItem('accessToken', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        this.accessToken = newAccessToken;
        this.lastRefreshAttemptAt = Date.now(); // Reset cooldown after a successful refresh
        return newAccessToken;
      } else {
        throw new Error('No access token in refresh response');
      }
    } catch (error) {
      console.error('AuthInterceptor: Token refresh failed:', error);

      // Check if another parallel request successfully refreshed the token
      const currentToken = localStorage.getItem('accessToken');
      if (!currentToken) {
        console.log('AuthInterceptor: Refresh failed and no token exists, handling auth error');
        this.handleAuthError();
      } else {
        console.log('AuthInterceptor: Refresh failed but token exists from parallel refresh');
      }

      return null;
    }
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(): void {
    console.log('AuthInterceptor: Handling auth error - clearing tokens');
    this.clearTokens();
    
    if (this.authErrorHandler) {
      console.log('AuthInterceptor: Calling custom auth error handler');
      this.authErrorHandler();
    } else {
      // Default behavior: redirect to login if not already there
      console.log('AuthInterceptor: No custom handler, using default redirect');
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && 
          currentPath !== '/register' && 
          !currentPath.includes('/oauth/')) {
        console.log('AuthInterceptor: Redirecting to /login');
        window.location.href = '/login';
      }
    }
  }

  /**
   * Set authentication tokens
   */
  setTokens(accessToken: string, refreshToken?: string): void {
    this.accessToken = accessToken;
    localStorage.setItem('accessToken', accessToken);
    
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  /**
   * Clear authentication tokens
   */
  clearTokens(): void {
    this.accessToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  /**
   * Check if user has valid authentication token
   */
  hasValidAuthToken(): boolean {
    return !!this.accessToken || !!localStorage.getItem('accessToken');
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.accessToken || localStorage.getItem('accessToken');
  }

  /**
   * Set auth error handler
   */
  setAuthErrorHandler(handler: (() => void) | null): void {
    this.authErrorHandler = handler;
  }

  /**
   * Cleanup interceptors
   */
  destroy(): void {
    if (this.requestInterceptorId !== null) {
      this.httpClient.removeRequestInterceptor(this.requestInterceptorId);
    }
    if (this.responseInterceptorId !== null) {
      this.httpClient.removeResponseInterceptor(this.responseInterceptorId);
    }
  }
}